import React, { useRef, useState, Fragment } from 'react';
import 'twin.macro';
import { Transition } from '@headlessui/react';
import { v4 as uuid } from 'uuid';
import { CustomFieldDefinition } from 'types/customField';
import { ROSelect } from 'containers/Eleves/List/Table/Cells/components/ROSelect';
import PortalWrapper from 'components/PortalWrapper';
import { autoUpdate, useFloating } from '@floating-ui/react-dom';
import { autoPlacement, inner } from '@floating-ui/react';
import { MultiselectCustomField } from 'components/NewCustomFields/MultiselectCustomField';
import useUpdateElevesCustomFields from 'queries/eleves/customFields/useUpdateElevesCustomFields';

interface Props {
  value: string[];
  onChange: (v: string[]) => void;
  customFields: CustomFieldDefinition[];
  fieldId: string;
  readonly?: boolean;
}

export function MultiselectCell(props: Props) {
  const { value, customFields, fieldId, readonly, onChange } = props;
  const customFieldDefinition = customFields.find(c => c.id === fieldId);
  const listRef = useRef<Array<HTMLElement | null>>([]);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const updateCustomFields = useUpdateElevesCustomFields();
  const [editing, setEditing] = useState<boolean>(false);
  const [localValue, setLocalValue] = useState<string[]>(value);
  const [localEditValue, setLocalEditValue] = useState<string[]>(value);

  const { refs, floatingStyles } = useFloating({
    middleware: [
      autoPlacement({
        crossAxis: true,
        alignment: 'start',
        autoAlignment: true,
        allowedPlacements: ['top-start', 'right-end', 'bottom-end', 'left-end'],
      }),
      inner({
        listRef,
        index: 0,
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  if (readonly) {
    return <div>{localEditValue.join(' • ')}</div>;
  }

  if (!customFieldDefinition) return null;
  return (
    <div tw="max-w-[18rem]" ref={refs.setReference}>
      <ROSelect onClick={() => setEditing(true)}>
        {localValue.join(' • ')}
      </ROSelect>
      {editing && (
        <PortalWrapper portal>
          <Transition show={editing} appear>
            <div tw="fixed inset-0 z-40">
              <Transition.Child
                as={Fragment as any}
                enter="overlayEnter"
                enterFrom="overlayEnterFrom"
                enterTo="overlayEnterTo"
                leave="overlayLeave"
                leaveFrom="overlayLeaveFrom"
                leaveTo="overlayLeaveTo"
              >
                <div
                  tw="absolute inset-0 bg-black/10 backdrop-blur-sm"
                  onClick={() => {
                    onChange(localEditValue);
                    setLocalValue(localEditValue);
                    setEditing(false);
                  }}
                />
              </Transition.Child>
              <div
                ref={node => {
                  refs.setFloating(node);
                  editorRef.current = node;
                }}
                style={floatingStyles}
              >
                <Transition.Child
                  enter="menuEnter"
                  enterFrom="menuEnterFrom"
                  enterTo="menuEnterTo"
                  leave="menuLeave"
                  leaveFrom="menuLeaveFrom"
                  leaveTo="menuLeaveTo"
                >
                  <MultiselectCustomField
                    options={customFieldDefinition.options || []}
                    value={localEditValue}
                    tw="p-5 bg-white shadow-lg rounded-md"
                    onChange={setLocalEditValue}
                    onCreateOption={v =>
                      updateCustomFields({
                        ...customFieldDefinition,
                        options: [
                          ...customFieldDefinition.options,
                          { id: uuid(), value: v },
                        ],
                      })
                    }
                    onRemoveOption={v =>
                      updateCustomFields({
                        ...customFieldDefinition,
                        options: customFieldDefinition.options.filter(
                          opt => opt.value !== v,
                        ),
                      })
                    }
                  />
                </Transition.Child>
              </div>
            </div>
          </Transition>
        </PortalWrapper>
      )}
    </div>
  );
}
